import { cn } from '@/lib/utils'
import { Copy, Download, Eye, FolderInput, Pencil, RotateCcw, Share2, Star, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef } from 'react'
import { FinderItem } from '../types'

interface ContextAction {
    id: string
    label: string
    icon: React.ReactNode
    danger?: boolean
}

interface FinderContextMenuProps {
    item: FinderItem
    position: { x: number; y: number }
    onClose: () => void
    onAction: (actionId: string, item: FinderItem) => void
    isTrashSection?: boolean
}

const normalActions: ContextAction[] = [
    { id: 'open', label: 'Open', icon: <Eye className='w-4 h-4' /> },
    { id: 'preview', label: 'Quick Look', icon: <Eye className='w-4 h-4' /> },
    { id: 'rename', label: 'Rename', icon: <Pencil className='w-4 h-4' /> },
    { id: 'duplicate', label: 'Duplicate', icon: <Copy className='w-4 h-4' /> },
    { id: 'favorite', label: 'Toggle Favorite', icon: <Star className='w-4 h-4' /> },
    { id: 'moveTo', label: 'Move to...', icon: <FolderInput className='w-4 h-4' /> },
    { id: 'share', label: 'Share', icon: <Share2 className='w-4 h-4' /> },
    { id: 'download', label: 'Download', icon: <Download className='w-4 h-4' /> },
    { id: 'trash', label: 'Move to Trash', icon: <Trash2 className='w-4 h-4' />, danger: true },
]

const trashActions: ContextAction[] = [
    { id: 'preview', label: 'Quick Look', icon: <Eye className='w-4 h-4' /> },
    { id: 'restore', label: 'Restore', icon: <RotateCcw className='w-4 h-4' /> },
    { id: 'deletePermanently', label: 'Delete Permanently', icon: <Trash2 className='w-4 h-4' />, danger: true },
]

export function FinderContextMenu({ item, position, onClose, onAction, isTrashSection = false }: FinderContextMenuProps) {
    const actions = useMemo(() => (isTrashSection ? trashActions : normalActions), [isTrashSection])
    const menuRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                onClose()
            }
        }

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }

        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('keydown', handleEscape)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('keydown', handleEscape)
        }
    }, [onClose])

    useEffect(() => {
        if (menuRef.current) {
            const rect = menuRef.current.getBoundingClientRect()
            const viewportWidth = window.innerWidth
            const viewportHeight = window.innerHeight

            if (rect.right > viewportWidth) {
                menuRef.current.style.left = `${position.x - rect.width}px`
            }
            if (rect.bottom > viewportHeight) {
                menuRef.current.style.top = `${position.y - rect.height}px`
            }
        }
    }, [position])

    return (
        <div className='fixed inset-0 z-50' onClick={onClose}>
            <div
                ref={menuRef}
                className='absolute w-44 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden py-1 animate-in fade-in zoom-in-95 duration-100'
                style={{ left: position.x, top: position.y }}
                onClick={e => e.stopPropagation()}
            >
                {actions.map((action, index) => (
                    <button
                        key={action.id}
                        onClick={() => {
                            onAction(action.id, item)
                            onClose()
                        }}
                        className={cn(
                            'w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors text-left',
                            action.danger
                                ? 'text-red-600 hover:bg-red-50'
                                : 'text-gray-700 hover:bg-gray-50 hover:text-[#63316B]',
                            index === 4 && 'border-t border-gray-100 mt-1 pt-2',
                            index === 7 && 'border-t border-gray-100 mt-1 pt-2'
                        )}
                    >
                        {action.icon}
                        {action.id === 'favorite' && item.favorite ? 'Remove Favorite' : action.label}
                    </button>
                ))}
            </div>
        </div>
    )
}
